/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import React, { useEffect, useRef, useState } from 'react'
import Hls from 'hls.js'
import colors from '@sportninja/common/constants/appColors'
import { Spin } from 'antd'

interface LivestreamPlayerProps {
  streamUrl: string | null
}

const isHlsUrl = (url: string): boolean => {
  return url.includes('.m3u8')
}

const LivestreamPlayer: React.FC<LivestreamPlayerProps> = ({ streamUrl }) => {
  const videoRef = useRef<HTMLVideoElement>(null)
  const hlsRef = useRef<Hls | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!streamUrl) {
      setError('No stream URL available')
      setLoading(false)
      return
    }

    setLoading(true)
    setError(null)

    const video = videoRef.current
    if (!video) {
      return
    }

    const handleCanPlay = () => {
      setLoading(false)
    }

    const handleError = () => {
      setError('Failed to load video')
      setLoading(false)
    }

    video.addEventListener('canplay', handleCanPlay)
    video.addEventListener('error', handleError)

    // Check if it's an HLS stream or a direct video file
    if (isHlsUrl(streamUrl)) {
      // HLS stream
      if (Hls.isSupported()) {
        const hls = new Hls({
          enableWorker: true,
          lowLatencyMode: true,
        })
        hlsRef.current = hls

        hls.loadSource(streamUrl)
        hls.attachMedia(video)

        hls.on(Hls.Events.MANIFEST_PARSED, () => {
          setLoading(false)
        })

        hls.on(Hls.Events.ERROR, (_, data) => {
          if (data.fatal) {
            switch (data.type) {
              case Hls.ErrorTypes.NETWORK_ERROR:
                setError('Network error - attempting to recover...')
                hls.startLoad()
                break
              case Hls.ErrorTypes.MEDIA_ERROR:
                setError('Media error - attempting to recover...')
                hls.recoverMediaError()
                break
              default:
                setError('Failed to load livestream')
                hls.destroy()
                break
            }
          }
        })
      } else if (video.canPlayType('application/vnd.apple.mpegurl')) {
        // Native HLS support (Safari)
        video.src = streamUrl
        video.addEventListener('loadedmetadata', () => {
          setLoading(false)
        })
      } else {
        setError('HLS playback is not supported in this browser')
        setLoading(false)
      }
    } else {
      // Direct video file (MP4, etc.)
      video.src = streamUrl
      video.addEventListener('loadedmetadata', () => {
        setLoading(false)
      })
    }

    return () => {
      video.removeEventListener('canplay', handleCanPlay)
      video.removeEventListener('error', handleError)

      if (hlsRef.current) {
        hlsRef.current.destroy()
        hlsRef.current = null
      }
    }
  }, [streamUrl])

  return (
    <div
      css={css`
        position: relative;
        width: 100%;
        aspect-ratio: 16/9;
        background: ${colors.BLACK};
        border-radius: 8px;
        overflow: hidden;
      `}
    >
      {loading && (
        <div
          css={css`
            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            display: flex;
            justify-content: center;
            align-items: center;
            background: ${colors.BLACK};
            z-index: 1;
          `}
        >
          <Spin
            size='large'
            css={css`
              .ant-spin-dot-item {
                background-color: ${colors.PRIMARY};
              }
            `}
          />
        </div>
      )}

      {error && !loading && (
        <div
          css={css`
            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            display: flex;
            justify-content: center;
            align-items: center;
            background: ${colors.BLACK};
            z-index: 1;
          `}
        >
          <span
            css={css`
              color: ${colors.NEUTRAL_70};
              font-size: 14px;
            `}
          >
            {error}
          </span>
        </div>
      )}

      <video
        ref={videoRef}
        controls
        playsInline
        css={css`
          width: 100%;
          height: 100%;
          object-fit: cover;
          opacity: ${loading || error ? 0 : 1};
        `}
      >
        Your browser does not support video playback.
      </video>
    </div>
  )
}

export default LivestreamPlayer
